import { useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { ArrowRight, Lock, Mail } from '@/components/icons';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { Button, Field, Input } from '@/components/ui';

function safeNext(next: string | null): string {
  // Only allow same-origin relative paths to avoid open redirects.
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

export function LoginPage() {
  const { user, signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = safeNext(params.get('next'));

  if (user) return <Navigate to={next} replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.login(email.trim(), password);
      await signIn(res.token);
      navigate(next, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="text-[32px]/10 font-semibold tracking-[-0.04em] text-fg">Welcome back</h1>
      <p className="mt-2 text-sm text-fg-secondary">Sign in to run and review your tests.</p>
      <form onSubmit={onSubmit} className="mt-8 space-y-5" noValidate>
        <Field label="Email">
          {(id) => (
            <div className="relative">
              <Mail className="pointer-events-none absolute top-1/2 left-4 size-3.5 -translate-y-1/2 text-fg-muted" />
              <Input
                id={id}
                type="email"
                autoComplete="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10"
                placeholder="you@company.com"
              />
            </div>
          )}
        </Field>
        <Field label="Password">
          {(id) => (
            <div className="relative">
              <Lock className="pointer-events-none absolute top-1/2 left-4 size-3.5 -translate-y-1/2 text-fg-muted" />
              <Input
                id={id}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10"
                placeholder="••••••••"
              />
            </div>
          )}
        </Field>
        {error && (
          <p role="alert" className="animate-fade-in rounded-field bg-danger-subtle px-4 py-3 text-sm text-danger-text ring-1 ring-danger-line ring-inset">
            {error}
          </p>
        )}
        <Button type="submit" size="lg" className="w-full" loading={busy} disabled={!email || !password}>
          Sign in <ArrowRight className="size-3" />
        </Button>
      </form>
      <p className="mt-8 text-center text-xs text-fg-muted">
        New here? Ask your 1QA admin or project admin for an invitation link.
      </p>
    </AuthLayout>
  );
}
